import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RateMatrixCellData} from '../types/RateMatrixCellData';
import {
  rateMatrixCellFormSchema,
  RateMatrixCellFormValues,
} from './schemas/rateMatrixCellFormSchema';

interface RateMatrixCellFormProps extends RequiredTestIdProps {
  initialData: RateMatrixCellData | undefined;
  onSubmit: (data: RateMatrixCellData) => void;
  onRemove: (() => void) | undefined;
}

export function RateMatrixCellForm(props: RateMatrixCellFormProps) {
  const defaultValues: RateMatrixCellFormValues = {
    nominalRate: props.initialData?.nominalRate ?? '',
    apr: props.initialData?.apr ?? '',
  };

  const handleSubmit: FormSubmitHandler<RateMatrixCellFormValues> = (values) => {
    props.onSubmit(values);
    closeCurrentDialog();
    return Promise.resolve();
  };

  const handleRemove = () => {
    props.onRemove?.();
    closeCurrentDialog();
  };

  return (
    <Form<RateMatrixCellFormValues>
      experimentalZodSchema={rateMatrixCellFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      data-testid={suffixTestId('cellForm', props)}
    >
      {(control) => (
        <VStack spacing={4}>
          <Grid columns={2}>
            <FormField
              control={control}
              name="nominalRate"
              type="text"
              isRequired
              suffix="%"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.nominalRate')}
              data-testid={suffixTestId('nominalRate', props)}
            />
            <FormField
              control={control}
              name="apr"
              type="text"
              isRequired
              suffix="%"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.apr')}
              data-testid={suffixTestId('apr', props)}
            />
          </Grid>
          <HStack align="center">
            <Show when={isNotNil(props.onRemove)}>
              <Button
                variant="dangerLink"
                title={i18n.t('general.actions.remove')}
                onClick={handleRemove}
                data-testid={suffixTestId('remove', props)}
              />
            </Show>
            <Box flexGrow={1}>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.cancel')}
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.actions.save')}
                  data-testid={suffixTestId('save', props)}
                />
              </ButtonGroup>
            </Box>
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
